import React, {useEffect, useMemo, useState} from 'react';
import { useParams } from "react-router-dom";
import { FaMapMarkerAlt } from "react-icons/fa";
import { useFormData, useJob } from "../../api/api";
import { useTranslation } from "react-i18next";
import { BlocksRenderer } from '@strapi/blocks-react-renderer';
import { Helmet } from "react-helmet";
import Error from "../404/Error";
import Container from "../../components/Container";
import Button from "../../components/Button";
import InnerPage from "../../components/InnerPage";
import Form from "../../components/Form";
import Thanks from "../../components/Form/Thanks";
import Preloader from "../../components/Preloader";
import BG from './assets/bg_job.webp';
import skull from '../../components/Form/assets/skull.png';
import styles from "./job.module.scss";
import Page from "../../components/Page";

const Job = () => {
  const { jobId } = useParams();
  const [t, i18n] = useTranslation("global");
  const lang = useMemo(() => i18n.language.slice(0, 2), [i18n.language]);
  const [errorRequest, setErrorRequest] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showThanks, setShowThanks] = useState(false);
  const [thanksImg, setThanksImg] = useState(skull);

  const { formFields, isFormFieldsLoading, formFieldsError} = useFormData(lang);
  const { jobData, isJobLoading, jobError } = useJob(jobId, lang);

  useEffect( () => {
    setThanksImg(skull);
  }, []);

  const onApply = () => {
    setShowForm(true);
  }

  if (isJobLoading || isFormFieldsLoading) {
    return <Preloader />
  }

  if (formFieldsError || formFields.error || jobError || jobData.error) {
    return <Error />
  }

  const data = jobData.data;
  const formData = formFields.data;

  return (
    <Page>
      <Helmet>
        <title>RedBark Studio | {data?.title}</title>
      </Helmet>
      <InnerPage scrollTop={[showForm, showThanks]} className={styles.wrapper} background={BG}>
        <Container className={styles.jobContainer}>
          { !showForm && !showThanks &&
            <>
              <div className="row flex-col-center-v">
                <div className="col-xl-8 col-lg-8 col-md-10 col-sm-12">
                  <h1 className={styles.title}>{data.title}</h1>
                </div>
              </div>

              <div className="row flex-col-center-v">
                <div className="col-lg-8 flex-col-center-v">
                  <div className={styles.field}>
                    <span className={styles.fieldTitle}><FaMapMarkerAlt /> {t("jobs.location")}</span>
                    <span>{data.location}</span>
                  </div>
                </div>
              </div>

              <div className="row flex-col-center-v">
                <div className="col-xl-8 col-lg-10 col-md-10 col-sm-12">
                  <div className={styles.description}>
                    <div className={styles.inner}>
                      { data.description && <BlocksRenderer content={data.description} /> }
                      { data.responsibilities &&
                        <>
                          <h3>{t("job_page.responsibilities")}</h3>
                          <BlocksRenderer content={data.responsibilities} />
                        </>
                      }
                      { data.requirements &&
                        <>
                          <h3>{t("job_page.requirements")}</h3>
                          <BlocksRenderer content={data.requirements} />
                        </>
                      }
                      { data.desirable &&
                        <>
                          <h3>{t("job_page.desirable")}</h3>
                          <BlocksRenderer content={data.desirable} />
                        </>
                      }
                      { data.we_offer &&
                        <>
                          <h3>{t("job_page.we_offer")}</h3>
                          <BlocksRenderer content={data.we_offer} />
                        </>
                      }
                    </div>
                    <Button text={t("buttons.apply")} view={'light'} onClick={onApply}/>
                  </div>
                </div>
              </div>
            </>
          }
          { showForm && !showThanks &&
            <div className="row flex-col-center-v">
              <div className="col-sm-10 col-md-8 col-xs-12">
                <Form
                  title={data.title}
                  setShowForm={setShowForm}
                  setShowThanks={setShowThanks}
                  setErrorRequest={setErrorRequest}
                  sendTo={formData.recruitment}
                />
              </div>
            </div>
          }

          <div className="row flex-col-center-v" style={{display: `${!(!showForm && showThanks && !isFormFieldsLoading && !isJobLoading) ? 'none' : 'flex'}`}}>
            <div className="col-lg-8">
              <Thanks
                title={t("form.thanks")}
                img={thanksImg}
                text={formData.thanks}
                setShowForm={setShowForm}
                setShowThanks={setShowThanks}
                errorRequest={errorRequest}
                errorTitle={t("form_errors.error_title")}
                errorText={t("form_errors.error_text")}
              />
            </div>
          </div>


        </Container>
      </InnerPage>
    </Page>
  );
};

export default Job;